import React, { useEffect, useState } from 'react';
import {
  Typography, Table, Button, Modal, Form, Input, Select, Switch, Space, Tag, Popconfirm, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';

// Admin > Business Config > Skill Groups: named sets of a campaign's skills,
// offered as the Skill Group filter on the call pages (Search, Analytics,
// Campaign Stats ...). admin / qa_admin.
export default function SkillGroupsAdminPage() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [modal, setModal]         = useState(null);   // null | { group }
  const [saving, setSaving]       = useState(false);
  const [skillOpts, setSkillOpts] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [form] = Form.useForm();
  const campaignId = Form.useWatch('campaign_id', form);

  const load = () => {
    setLoading(true);
    api.get('/api/skill-groups', { params: { all: 1 } })
      .then((r) => setRows(r.data || []))
      .catch(() => message.error('Failed to load skill groups.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get('/api/admin/campaigns').then((r) => setCampaigns(r.data || [])).catch(() => {});
  }, []);

  // The picked campaign's skills (the names it has calls on), plus any the
  // group already has that no longer show up there.
  useEffect(() => {
    if (!modal || !campaignId) { setSkillOpts([]); return; }
    setSkillsLoading(true);
    api.get('/api/skill-groups/skill-options', { params: { campaign_id: campaignId } })
      .then((r) => setSkillOpts(r.data || []))
      .catch(() => setSkillOpts([]))
      .finally(() => setSkillsLoading(false));
  }, [modal, campaignId]);

  const open = (group) => setModal({ group });

  // Fill the form for the group clicked, every time the popup opens: the
  // form instance outlives the popup, so its starting values alone could
  // show the group edited before.
  useEffect(() => {
    if (!modal) return;
    form.resetFields();
    form.setFieldsValue({
      name: modal.group?.Name || '',
      campaign_id: modal.group?.CampaignID,
      skills: modal.group?.Skills || [],
    });
  }, [modal, form]);

  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = { name: v.name, campaign_id: v.campaign_id, skills: v.skills };
      if (modal.group) await api.put(`/api/skill-groups/${modal.group.ID}`, body);
      else             await api.post('/api/skill-groups', body);
      setModal(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (g, on) => {
    try { await api.put(`/api/skill-groups/${g.ID}`, { is_active: on }); load(); }
    catch { message.error('Failed to update.'); }
  };
  const remove = async (g) => {
    try { await api.delete(`/api/skill-groups/${g.ID}`); load(); }
    catch { message.error('Delete failed.'); }
  };

  const keep = modal?.group?.Skills || [];
  const options = [...new Set([...skillOpts, ...keep])].sort().map((x) => ({ value: x, label: x }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Skill Groups</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>Add Skill Group</Button>
      </div>
      <Table
        rowKey="ID"
        dataSource={rows}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No skill groups yet.' }}
        columns={[
          { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 180,
            sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
          { title: 'Skill Group', dataIndex: 'Name', key: 'Name', width: 220,
            sorter: (a, b) => a.Name.localeCompare(b.Name) },
          { title: 'Skills', dataIndex: 'Skills', key: 'Skills',
            render: (v) => <Space size={[4, 4]} wrap>{(v || []).map((x) => <Tag key={x}>{x}</Tag>)}</Space> },
          { title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 80,
            render: (v, g) => <Switch size="small" checked={!!v} onChange={(on) => toggleActive(g, on)} /> },
          { title: '', key: 'actions', width: 120, align: 'right',
            render: (_, g) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => open(g)}>Edit</Button>
                <Popconfirm title="Delete this skill group?" okText="Delete" okButtonProps={{ danger: true }}
                  onConfirm={() => remove(g)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ) },
        ]}
      />

      <Modal maskClosable={false}
        title={modal?.group ? 'Edit Skill Group' : 'Add Skill Group'}
        open={!!modal}
        onOk={save}
        onCancel={() => setModal(null)}
        confirmLoading={saving}
        okText="Save"
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="Campaign" name="campaign_id" rules={[{ required: true, message: 'Campaign is required' }]}>
            <Select
              showSearch optionFilterProp="label"
              options={campaigns.filter((c) => c.IsActive || c.ID === modal?.group?.CampaignID)
                .map((c) => ({ value: c.ID, label: c.Name }))}
              onChange={() => form.setFieldValue('skills', [])}
            />
          </Form.Item>
          <Form.Item label="Skills" name="skills"
            rules={[{ required: true, type: 'array', min: 1, message: 'Pick at least one skill' }]}>
            <Select mode="multiple" allowClear showSearch loading={skillsLoading} disabled={!campaignId}
              optionFilterProp="label" options={options} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
